import { useState, useEffect, useRef } from "react";
import { SidebarDataTest } from "../../data/json/sidebarMenu";
import {
  FlatMenuItem,
  generateSuggestions,
  flattenMenus,
  findBestMenuMatch,
} from "../aiMenuSearch";

export const useAISidebarSearch = () => {
  const [allMenus, setAllMenus] = useState<FlatMenuItem[]>([]);
  const [suggestions, setSuggestions] = useState<FlatMenuItem[]>([]);
  const [loading, setLoading] = useState(false); 

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const flat = flattenMenus(SidebarDataTest);
    setAllMenus(flat);
  }, []);

  const updateSuggestionsList = (query: string) => {
    // Clear previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    // If query is empty, reset instantly
    if (!query.trim()) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    // Show loading immediately (feels responsive)
    setLoading(true);

    // Debounce heavy search (feels like real AI processing)
    debounceTimeout.current = setTimeout(() => {
      const list = generateSuggestions(allMenus, query);
      setSuggestions(list);
      setLoading(false);
    }, 300); // 300ms feels natural — like AI is "thinking"
  };

  const searchMenus = (query: string): FlatMenuItem | null => {
    if (!query.trim()) return null;
    return findBestMenuMatch(allMenus, query);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return {
    suggestions,
    updateSuggestionsList,
    searchMenus,
    loading, // ← Now exported!
  };
};